import * as THREE from 'three';
import { basic, emis, std, stone } from '../materials.ts';
import { M, V, glowSprite, Ribbon, wisp } from '../prims.ts';
import { kit } from './era4-kit.ts';
import { begin, bonesOf, done, joint, type BaseOpts, type Model } from './types.ts';

/*
 * El Gusano de Médula (the Chasm's Ribs guardian, docs/SIMA.md «Guardianes»): «Se hunde y sale al final de la
 * grieta: esperalo al costado.» A worm as long as a wall, armoured in the plates of the titan's old bones: each
 * ring of dark red flesh carries a grey dorsal plate with two spines and ribs curling down its flanks, and the
 * marrow's red light seeps between the rings. Its front rears like a serpent's; its head is a collar of bone
 * round a maw that opens in three jaws lined with teeth, the throat burning red.
 *
 * How the integrator drives it (anim/creatures/marrow-worm.ts): 'dive' (1.2 s) rears and plunges head first into
 * the floor (all of it under by 1.1 s: swap to `marrowWormBurrowed` then); 'surface' (1.3 s) bursts up out of the
 * floor at the crack's end, jaws wide, and crashes down (0.8 s, the blow that lifts everything round); 'stun'
 * (also 'dazed') is it lying stunned on the floor where it fell, jaws slack. `marrowWormBurrowed` is the mound
 * that runs along the crack while it is under (clips 'idle', 'walk', 'die').
 *
 * Bones: body (the ring where the rearing starts), tail1–tail4 behind it in a chain, neck1, neck2, head, and the
 * three jaws jawA (top), jawB, jawC, each opening by −X in its own frame. Faces +Z.
 */

export const MARROW_WORM_HEIGHT = 2.5;

/** A bone at `at` whose frame is turned by `q` (its local axes are q's); its parts keep absolute coordinates. */
const qjoint = (parentAbs: THREE.Object3D, at: THREE.Vector3, q: THREE.Quaternion, name: string): { p: THREE.Group; abs: THREE.Group } => {
  const p = new THREE.Group();
  p.name = name;
  p.position.copy(at);
  p.quaternion.copy(q);
  parentAbs.add(p);
  const abs = new THREE.Group();
  const inv = q.clone().invert();
  abs.quaternion.copy(inv);
  abs.position.copy(at).negate().applyQuaternion(inv);
  p.add(abs);
  return { p, abs };
};

const mats = (): Record<string, THREE.Material> => ({
  flesh: std('#4a1a20', { roughness: 0.45, bumpScale: 0.03 }),
  fleshL: std('#7a3036', { roughness: 0.5 }),
  bone: std('#80868e', { roughness: 0.75, flatShading: true, bumpScale: 0.05 }),
  boneD: std('#4b4f56', { roughness: 0.8, flatShading: true }),
  marrow: emis('#ff5a64', '#d0464f', 1.6),
  teeth: std('#b4b9bf', { roughness: 0.45, flatShading: true }),
  earth: stone('#2d2a2b'),
  earthL: stone('#46403f'),
  dust: basic('#6b6262', 0.3),
});

export const marrowWorm = (o?: BaseOpts): Model => {
  const c = begin(o, 1.5, 4747);
  const { g, rng } = c;
  const k4 = kit(rng);
  const m = mats();

  // The spine, tail to head, and how thick each ring is.
  const S = [V(0, 0.22, -2.75), V(0, 0.3, -2.2), V(0, 0.38, -1.6), V(0, 0.45, -0.95), V(0, 0.5, -0.28), V(0, 0.9, 0.28), V(0, 1.52, 0.52), V(0, 2.12, 0.6)];
  const RAD = [0.2, 0.3, 0.38, 0.45, 0.5, 0.48, 0.45, 0.42];
  const dirAt = (i: number): THREE.Vector3 => (S[Math.min(S.length - 1, i + 1)]!.clone().sub(S[Math.max(0, i - 1)]!)).normalize();

  /** One armoured ring: the flesh, a dorsal plate with two spines, ribs down its flanks, the marrow's light at its seam. */
  const ring = (parent: THREE.Object3D, i: number): void => {
    const p = S[i]!, r = RAD[i]!;
    const q = new THREE.Quaternion().setFromUnitVectors(V(0, 0, 1), dirAt(i));
    const at = (x: number, y: number, z: number): THREE.Vector3 => p.clone().add(V(x, y, z).applyQuaternion(q));
    const fl = M(new THREE.SphereGeometry(1, 10, 7), m.flesh!, parent);
    fl.position.copy(p);
    fl.scale.set(r * 1.0, r * 0.88, r * 0.8);
    fl.quaternion.copy(q);
    const be = M(new THREE.SphereGeometry(1, 7, 4), m.fleshL!, parent);
    be.position.copy(at(0, -r * 0.3, 0));
    be.scale.set(r * 0.82, r * 0.6, r * 0.7);
    be.quaternion.copy(q);
    // The dorsal plate: a shell of bone over the top.
    const pl = M(new THREE.SphereGeometry(1, 8, 4, 0, Math.PI * 2, 0, Math.PI * 0.36), m.bone!, parent);
    pl.position.copy(at(0, r * 0.14, -r * 0.05));
    pl.scale.set(r * 1.0, r * 0.9, r * 0.86);
    // A keel of bone along the plate's crest.
    const keel = M(new THREE.BoxGeometry(r * 0.16, r * 0.14, r * 1.1), m.boneD!, parent);
    keel.position.copy(at(0, r * 1.02, -r * 0.05));
    keel.quaternion.copy(q);
    pl.quaternion.copy(q);
    for (const k of [-1, 1]) {
      const sp = M(new THREE.ConeGeometry(r * 0.12, r * 0.6, 5), m.boneD!, parent);
      sp.position.copy(at(k * r * 0.28, r * 1.05, -r * 0.1));
      sp.quaternion.copy(q).multiply(new THREE.Quaternion().setFromEuler(new THREE.Euler(-0.5, 0, -k * 0.35)));
      // Ribs curling down the flank.
      k4.tube(parent, [at(k * r * 0.75, r * 0.55, r * 0.15), at(k * r * 1.08, r * 0.05, r * 0.2), at(k * r * 0.95, -r * 0.45, r * 0.05)], r * 0.07, m.bone!, 4);
      k4.tube(parent, [at(k * r * 0.75, r * 0.55, -r * 0.3), at(k * r * 1.06, 0, -r * 0.3), at(k * r * 0.9, -r * 0.5, -r * 0.42)], r * 0.06, m.boneD!, 4);
    }
    // The seam behind the ring, the marrow's red light in it.
    if (i > 0) {
      const sm = M(new THREE.TorusGeometry(1, 0.05, 4, 16), m.marrow!, parent);
      sm.position.copy(at(0, 0, -r * 0.62));
      sm.scale.set(r * 0.9, r * 0.8, 1);
      sm.quaternion.copy(q);
    }
    if (i >= 2) glowSprite(parent, '#d0464f', r * 1.4, at(0, r * 0.4, -r * 0.6), 0.18);
  };

  /* ---------- the body and the tail behind it ---------- */
  const bodyB = joint(g, S[4]!, 'body');
  ring(bodyB.abs, 4);
  let prev = bodyB.abs;
  const tails: THREE.Object3D[] = [];
  for (let i = 3; i >= 0; i--) {
    const tb = joint(prev, S[i]!.clone().lerp(S[i + 1]!, 0.45), `tail${4 - i}`);
    ring(tb.abs, i);
    tails.push(tb.p);
    prev = tb.abs;
  }
  // The tail ends in a point of bone.
  const tip = M(new THREE.ConeGeometry(0.14, 0.45, 6), m.bone!, prev);
  tip.position.set(0, 0.2, -3.05);
  tip.rotation.x = -Math.PI / 2;

  /* ---------- the neck rearing ---------- */
  const n1 = joint(bodyB.abs, S[5]!.clone().lerp(S[4]!, 0.5), 'neck1');
  ring(n1.abs, 5);
  const n2 = joint(n1.abs, S[6]!.clone().lerp(S[5]!, 0.5), 'neck2');
  ring(n2.abs, 6);
  const hB = joint(n2.abs, S[7]!.clone().lerp(S[6]!, 0.5), 'head');
  const hd = hB.abs;
  ring(hd, 7);

  /* ---------- the head: a collar of bone, a maw of three jaws ---------- */
  const mawDir = V(0, -0.25, 1).normalize();
  const mawAt = V(0, 2.28, 0.86);
  const qm = new THREE.Quaternion().setFromUnitVectors(V(0, 0, 1), mawDir);
  const collar = M(new THREE.TorusGeometry(0.36, 0.09, 6, 16), m.bone!, hd);
  collar.position.copy(mawAt).addScaledVector(mawDir, -0.06);
  collar.quaternion.copy(qm);
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2;
    const rad = V(Math.cos(a), Math.sin(a), 0).applyQuaternion(qm);
    const sp = M(new THREE.ConeGeometry(0.04, 0.2, 4), m.boneD!, hd);
    sp.position.copy(mawAt).addScaledVector(rad, 0.42).addScaledVector(mawDir, -0.1);
    sp.quaternion.setFromUnitVectors(V(0, 1, 0), rad.clone().addScaledVector(mawDir, -0.5).normalize());
  }
  // The throat: red light and rings of teeth pointing in.
  const throat = M(new THREE.CircleGeometry(0.3, 14), m.marrow!, hd);
  throat.position.copy(mawAt).addScaledVector(mawDir, -0.04);
  throat.quaternion.copy(qm);
  glowSprite(hd, '#ff5a64', 0.9, mawAt.clone().addScaledVector(mawDir, 0.05), 0.55);
  for (const [r, n, len] of [[0.27, 12, 0.1], [0.17, 9, 0.08]] as const) {
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + r * 5;
      const rad = V(Math.cos(a), Math.sin(a), 0).applyQuaternion(qm);
      const t = M(new THREE.ConeGeometry(0.025, len, 4), m.teeth!, hd);
      t.position.copy(mawAt).addScaledVector(rad, r).addScaledVector(mawDir, r === 0.27 ? 0 : -0.03);
      t.quaternion.setFromUnitVectors(V(0, 1, 0), rad.clone().negate().addScaledVector(mawDir, 0.3).normalize());
    }
  }
  // The three jaws: curved plates of bone meeting in a point before the maw, teeth along their inner edge.
  const jaws: THREE.Object3D[] = [];
  for (let j = 0; j < 3; j++) {
    const th = Math.PI / 2 + (j * Math.PI * 2) / 3;
    const qz = qm.clone().multiply(new THREE.Quaternion().setFromAxisAngle(V(0, 0, 1), th - Math.PI / 2));
    const rad = V(0, 1, 0).applyQuaternion(qz);
    const base = mawAt.clone().addScaledVector(rad, 0.3);
    const jb = qjoint(hd, base, qz, `jaw${'ABC'[j]}`);
    const tipP = mawAt.clone().addScaledVector(mawDir, 0.55).addScaledVector(rad, 0.03);
    const mid = mawAt.clone().addScaledVector(mawDir, 0.28).addScaledVector(rad, 0.28);
    k4.tube(jb.abs, [base, mid, tipP], 0.07, m.bone!, 5);
    const plate = M(new THREE.SphereGeometry(1, 8, 5, 0, Math.PI * 2, 0, Math.PI / 2), m.boneD!, jb.abs);
    plate.position.copy(base.clone().lerp(tipP, 0.4)).addScaledVector(rad, 0.04);
    plate.quaternion.setFromUnitVectors(V(0, 1, 0), rad);
    plate.scale.set(0.2, 0.08, 0.3);
    for (let i = 0; i < 3; i++) {
      const at = base.clone().lerp(tipP, 0.25 + i * 0.22).addScaledVector(rad, -0.05);
      const t = M(new THREE.ConeGeometry(0.022, 0.09, 4), m.teeth!, jb.abs);
      t.position.copy(at);
      t.quaternion.setFromUnitVectors(V(0, 1, 0), rad.clone().negate());
    }
    jaws.push(jb.p);
  }

  return done(c, MARROW_WORM_HEIGHT, () => undefined, undefined, bonesOf(c, {
    body: bodyB.p, tail1: tails[0]!, tail2: tails[1]!, tail3: tails[2]!, tail4: tails[3]!, neck1: n1.p, neck2: n2.p, head: hB.p,
    jawA: jaws[0]!, jawB: jaws[1]!, jawC: jaws[2]!,
  }));
};

/** The worm under the floor: a mound of broken stone and bone heaving along, its back plates breaking through. */
export const marrowWormBurrowed = (o?: BaseOpts): Model => {
  const c = begin(o, 0.9, 4749);
  const { g, rng } = c;
  const k4 = kit(rng);
  const m = mats();

  const ridgeB = joint(g, V(0, 0, 0), 'ridge');
  const rd = ridgeB.abs;
  for (let i = 0; i < 10; i++) {
    const z = 0.6 - i * 0.17;
    const h = Math.max(0.07, 0.3 - i * 0.024);
    const w = 0.42 - i * 0.025;
    for (const k of [-1, 1]) k4.lump(rd, (i + (k > 0 ? 1 : 0)) % 2 ? m.earth! : m.earthL!, k * w * 0.42, h * 0.4, z + k * 0.03, w * 0.55, h * 0.75, 0.14, 0.6, 0);
  }
  // Slabs of floor turned up either side of the head of the mound.
  for (const k of [-1, 1]) {
    for (let i = 0; i < 2; i++) {
      const slab = M(new THREE.BoxGeometry(0.3, 0.05, 0.22), i ? m.earth! : m.earthL!, rd);
      slab.position.set(k * (0.36 + i * 0.08), 0.1, 0.6 - i * 0.22);
      slab.rotation.set(0.3, 0, -k * (0.9 + i * 0.2));
    }
  }
  // The crack along the crest, the marrow's red seeping up.
  k4.crack(rd, [V(-0.1, 0.32, 0.72), V(0, 0.35, 0.55), V(0.05, 0.31, 0.36), V(-0.02, 0.26, 0.15)], 0.018, m.marrow!, 0.04);
  glowSprite(rd, '#d0464f', 0.7, V(0, 0.36, 0.55), 0.45);
  for (let i = 0; i < 9; i++) {
    const k = i % 2 ? 1 : -1;
    k4.lump(rd, m.earth!, k * (0.36 + rng() * 0.16), 0.04, 0.55 - i * 0.16, 0.06, 0.04, 0.06, 0.5, 0);
  }
  // Its back plates breaking through the crest, spines raking back.
  const platesB = joint(rd, V(0, 0.22, 0), 'plates');
  const pl = platesB.abs;
  for (const [z, s, y] of [[0.4, 0.17, 0.3], [0.12, 0.2, 0.28], [-0.18, 0.17, 0.23], [-0.48, 0.13, 0.17]] as const) {
    const sh = M(new THREE.SphereGeometry(s, 8, 4, 0, Math.PI * 2, 0, Math.PI * 0.42), m.bone!, pl);
    sh.position.set(0, y, z);
    sh.scale.set(1.1, 0.7, 1);
    for (const k of [-1, 1]) {
      const sp = M(new THREE.ConeGeometry(s * 0.14, s * 0.8, 5), m.boneD!, pl);
      sp.position.set(k * s * 0.3, y + s * 0.6, z - 0.02);
      sp.rotation.set(-0.6, 0, -k * 0.3);
    }
  }
  // Dust boiling off it.
  const dust: Ribbon[] = [];
  for (const [x, z, h] of [[0.3, 0.55, 0.45], [-0.28, 0.45, 0.4], [0.05, 0.75, 0.35]] as const) dust.push(new Ribbon(rd, wisp(x, z, h, 4), 0.07, m.dust!, rng, 0.05));

  return done(c, 0.5, (n) => {
    if (n % 2 === 0) for (const d of dust) d.step();
  }, undefined, bonesOf(c, { ridge: ridgeB.p, plates: platesB.p }));
};
